import React, { useRef, useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { filterBy } from "@progress/kendo-data-query";
import { searchObjects } from "../../../api/commonApi";
import { HighlightText } from "../../matteroomTab/HighlightText";
import { serializeGhostTemplateText, useGhostTemplateValueSerializer } from "@features/timeEntries/hooks/useGhostTemplateValueSerializer";
import { isNarrativeOcgFeatureEnabled, normalizeTimesheetValidationResponse, validateTimesheetNarrative } from "@features/narrativeOcg";

const TEMPLATE_NARRATIVE_SEARCH_MAX_CHARS = 80;

const formatParticipantNames = participants =>
  (Array.isArray(participants) ? participants : [])
    .map(participant =>
      typeof participant === "string"
        ? participant
        : participant?.name || participant?.username
    )
    .filter(Boolean)
    .join(", ");

const GhostTemplateEditor = forwardRef(({ defaultValue, onChange, item, onValidationChange, onBlockOutsideClose, isFormInline = false, autoFocus = true, setIsEditing, showTemplateShortcutHint = false, validateOnBlur = false, disabled = false }, ref) => {
  const editorRef = useRef(null);
  const activeSpanRef = useRef(null);
  const insertedValueNodeRef = useRef(null);
  const [currentHint, setCurrentHint] = useState("");
  const [editorDisplayText, setEditorDisplayText] = useState(defaultValue || "");
  const [suggestions, setSuggestions] = useState([]);
  const suggestionsRef = useRef([]);
  const [activeVar, setActiveVar] = useState(null);
  const [showSearchBox, setShowSearchBox] = useState(false);
  const [searchBoxPos, setSearchBoxPos] = useState({ top: 0, left: 0 });
  const [search, setSearch] = useState("");
  const [filterContactNameOptions, setFilterContactNameOptions] = useState([]);
  const [filterMatterNameOptions, setFilterMatterNameOptions] = useState([]);
  const [filterEntityNameOptions, setFilterEntityNameOptions] = useState([]);
  const [isComposing, setIsComposing] = useState(false);
  const isInternalChange = useRef(false);
  const searchNarrativeTimerRef = useRef(null);
  const searchNarrativeRequestRef = useRef(0);
  const lastEmittedValueRef = useRef(defaultValue || "");
  const { getInternalValue } = useGhostTemplateValueSerializer(editorRef);

  const [isFocused, setIsFocused] = useState(false);

  const settings = JSON.parse(sessionStorage.getItem("settings") || "[]");
  const narrativeOcgEnabled = isNarrativeOcgFeatureEnabled(settings);

  useImperativeHandle(ref, () => ({
    clear() {
      lastEmittedValueRef.current = "";
      onChange({ value: "" });
      if (editorRef.current) {
        editorRef.current.innerText = "";
      }
      setEditorDisplayText("");
    },
    flush() {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      const value = getInternalValue();
      const capitalized = value.charAt(0).toUpperCase() + value.slice(1);
      if (lastEmittedValueRef.current !== capitalized) {
        lastEmittedValueRef.current = capitalized;
        onChange({ value: capitalized });
      }
      return capitalized;
    },
    getValue() {
      return getInternalValue();
    },
    setValue(nextValue) {
      renderTextWithVariables(nextValue || "");
      updateHintState(nextValue || "");
      lastEmittedValueRef.current = nextValue || "";
      onChange({ value: nextValue || "" });
    }
  }));

  // --------- 整合 variableMap ---------
  const apps = item?.apps ?? [];
  const type = apps.includes("Outlook") || apps.includes("Teams") ? "subject" : apps.includes("Word") || apps.includes("Excel") || apps.includes("PowerPoint") || apps.includes("PDF") ? "document" : "topic";

  const variableMap = {
    ct__: {
      options: filterContactNameOptions,
      setOptions: setFilterContactNameOptions,
      fq: "ct:(contact)",
      borderColor: "#FFD476",
      hoverColor: "rgba(255, 212, 118, 0.20)",
      activeColor: "#FFD476",
      label: "Select Contact",
      searchAble: true,
      replaceData: formatParticipantNames(item?.metadata?.participants)
    },
    mn__: {
      options: filterMatterNameOptions,
      setOptions: setFilterMatterNameOptions,
      fq: "ct:(matter)",
      borderColor: "#90CBF9",
      hoverColor: "rgba(144, 203, 249, 0.20)",
      activeColor: "#90CBF9",
      label: "Select Matter",
      searchAble: true,
      replaceData: item?.matter_name
    },
    cn__: {
      options: filterEntityNameOptions,
      setOptions: setFilterEntityNameOptions,
      fq: "ct:(client)",
      borderColor: "#D9ADE1",
      hoverColor: "rgba(217, 173, 225, 0.20)",
      activeColor: "#D9ADE1",
      label: "Select Client",
      searchAble: true,
      replaceData: item?.client_name
    },
    __activity_title__: {
      options: null,
      setOptions: null,
      fq: "",
      borderColor: "#B7AEC6",
      hoverColor: "#525252",
      activeColor: "#525252",
      label: type,
      searchAble: false,
      replaceData: item?.metadata?.activity_title
    }
  };

  const variableKeys = Object.keys(variableMap);

  useEffect(() => {
    if (defaultValue?.length) {
      renderTextWithVariables(defaultValue);
      lastEmittedValueRef.current = defaultValue;
      onChange({ value: defaultValue });
    }
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (activeSpanRef.current && !editorRef.current?.contains(activeSpanRef.current)) {
        activeSpanRef.current = null;
        setShowSearchBox(false);
        setActiveVar(null);
      }
    });

    if (editorRef.current) {
      observer.observe(editorRef.current, {
        childList: true,
        subtree: true
      });
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!editorRef.current) return;

    // 攔截 A：如果是內部打字觸發的同步，直接跳過，不准重畫 DOM！
    if (isInternalChange.current) return;

    const currentInternal = getInternalValue();

    // 攔截 B：即使鎖定失效了，只要內容「邏輯上」一樣，也絕對不准重畫！
    // 這裡用 .toLowerCase() 和 .replace(/\s+/g, "") 是為了抵銷大寫校正和空格的差異
    const normalize = s =>
      (s || "")
        .toLowerCase()
        .replace(/\s+/g, "")
        .replace(/[\u200b\u00a0]/g, "");
    if (normalize(defaultValue) === normalize(currentInternal)) return;

    // 只有真的內容完全不同（例如切換了整篇文案），才執行重畫
    renderTextWithVariables(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    if (editorRef.current && autoFocus) {
      editorRef.current.focus();

      // 可选：光标放到最后
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, [autoFocus]);

  useEffect(() => {
    syncVariableSpanStyles();
  }, [activeVar, showSearchBox]);

  // --------- 搜索 template narrative ---------
  function escapeSolrSpecialChars(keyword) {
    if (!keyword) return "";

    keyword = keyword.replace(/ /g, "\\ ");

    // Solr 特殊字符（不含 &）
    keyword = keyword.replace(/([+&|!(){}[\]^"~*?:\\\s-])/g, "\\$1").replace(/\//g, "\\/");

    // & → \ %26，斜線緊貼前一個字
    keyword = keyword
      .replace(/&/g, "%26")
      .replace(/\+/g, "%2B")
      .replace(/%(?!26|2B)/g, "%5C%25"); // 不替换已经被转义的 %26 或 %2B

    return keyword;
  }

  const callSearchNarrative = async requestId => {
    const raw = editorRef.current?.textContent || "";
    const keyword = raw
      .replace(/\u00A0/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, TEMPLATE_NARRATIVE_SEARCH_MAX_CHARS);

    let extraConditions = `(description:(*${escapeSolrSpecialChars(keyword)}*))`;
    if (item) {
      if (item.activity) {
        extraConditions += ` AND activity:(${item.activity})`;
      }
      if (item.source) {
        extraConditions += ` AND source_id:${item.source}`;
      }
    }
    const searchRes = await searchObjects("templatenarrative", keyword, 0, 40, "obj_id,description,activity,source_id,updated_on", extraConditions, false);

    if (requestId !== searchNarrativeRequestRef.current) return;

    const docs = searchRes ?? [];
    let result = keyword.length ? docs.map(d => d.description) : [];

    // 合并任意重复的 ct__
    result = result.map(
      text => text.replace(/(ct__)(\s*,\s*ct__)+/g, "$1").replace(/ct__\s*,/g, "ct__") // 去掉后面多余的逗号
    );

    suggestionsRef.current = result;
    setSuggestions(result);
    updateGhost();
  };

  const scheduleSearchNarrative = () => {
    if (searchNarrativeTimerRef.current) {
      clearTimeout(searchNarrativeTimerRef.current);
    }

    const requestId = ++searchNarrativeRequestRef.current;
    searchNarrativeTimerRef.current = setTimeout(() => {
      callSearchNarrative(requestId);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (searchNarrativeTimerRef.current) {
        clearTimeout(searchNarrativeTimerRef.current);
      }
      searchNarrativeRequestRef.current += 1;
    };
  }, []);

  // --------- 搜索變數值 ---------
  const callSearchVariable = async (varName, keyword) => {
    if (!variableMap[varName]) return;
    const fq = variableMap[varName].fq;
    const fields = varName === "mn__" ? "obj_id,name,number,client_name" : "obj_id,name,number";
    const searchRes = await searchObjects(null, keyword, 0, 40, fields, null, false, fq);
    variableMap[varName].setOptions(searchRes.data?.response?.docs ?? searchRes ?? []);
  };

  // --------- 將文字拆成 span ---------
  const renderTextWithVariables = (text, referenceNode = null) => {
    const metadata = item?.metadata || {};
    const narrativeVars = Array.isArray(metadata.narrativeVariables) ? metadata.narrativeVariables : [];

    if (!editorRef.current) return;
    const parent = editorRef.current;
    if (!referenceNode) parent.innerHTML = "";

    const metaMap = {};
    narrativeVars.forEach(v => {
      if (!metaMap[v.value]) metaMap[v.value] = v.placeholder;
    });

    const regexParts = [...variableKeys, ...Object.keys(metaMap)].map(s => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    const regex = regexParts.length ? new RegExp(`(${regexParts.join("|")})`, "g") : null;

    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    let match;

    if (regex) {
      while ((match = regex.exec(text)) !== null) {
        const before = text.slice(lastIndex, match.index);
        if (before) fragment.appendChild(document.createTextNode(before));

        const val = match[0];
        let placeholder = metaMap[val] || (variableKeys.includes(val) ? val : null);

        if (placeholder) {
          const meta = variableMap[placeholder];
          let displayText = "";
          let searchAble = variableMap[val]?.searchAble;

          if (variableMap[placeholder]?.searchAble) {
            if (val && val !== placeholder) {
              displayText = val;
              searchAble = false;
            } else {
              displayText = variableMap[placeholder]?.label;
              searchAble = true;
            }
          } else {
            displayText = meta?.replaceData ?? meta?.label;
          }

          const span = createVariableSpan(displayText, placeholder, searchAble);

          fragment.appendChild(span);
          fragment.appendChild(document.createTextNode("\u200B"));
        } else {
          fragment.appendChild(document.createTextNode(val));
        }

        lastIndex = regex.lastIndex;
      }
    }

    const after = text.slice(lastIndex);
    if (after) fragment.appendChild(document.createTextNode(after));

    if (referenceNode) parent.insertBefore(fragment, referenceNode.nextSibling);
    else parent.appendChild(fragment);

    setEditorDisplayText(parent.innerText || "");
    placeCursorAtEnd(fragment);
  };

  // --------- 建立 span ---------
  const createVariableSpan = (value, placeholder, searchAble = false) => {
    const span = document.createElement("span");
    const varStyle = variableMap[placeholder] || {};
    if (searchAble) {
      span.textContent = varStyle.label || value;
      if (value) {
        span.textContent = value;
      }
    } else {
      span.textContent = value || varStyle.label;
    }

    span.dataset.varSpan = "1";
    span.dataset.placeholder = placeholder;
    span.dataset.searchAble = searchAble ? "1" : "0";

    span.contentEditable = "false";

    Object.assign(span.style, {
      borderBottom: `3px solid ${varStyle.borderColor || ""}`,
      padding: "2px 22px 0px 4px", // 固定空间
      transition: "padding-right 0.2s",
      margin: "0px 2px 2px 0px",
      cursor: searchAble ? "pointer" : "default",
      alignItems: "center",
      position: "relative",
      background: "transparent",
      display: "inline-block",
      color: value == "ct__" ? "#958EA4" : "#333",
      whiteSpace: "nowrap",
      verticalAlign: "baseline"
    });

    /* ---------- 放大鏡 ---------- */
    let icon = null;
    if (searchAble) {
      icon = document.createElement("span");
      icon.dataset.varIcon = "1";
      icon.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
          <path d="M8.49297 7.875H9.11367L12.25 11.0113L11.0113 12.25L7.875 9.11367V8.49297L7.14492 7.76289C6.39023 8.38086 5.425 8.75 4.375 8.75C1.95781 8.75 0 6.79219 0 4.375C0 1.95781 1.95781 0 4.375 0C6.79219 0 8.75 1.95781 8.75 4.375C8.75 5.425 8.38086 6.39023 7.76289 7.14492L8.49297 7.875ZM4.375 0.875C2.4418 0.875 0.875 2.4418 0.875 4.375C0.875 6.3082 2.4418 7.875 4.375 7.875C6.3082 7.875 7.875 6.3082 7.875 4.375C7.875 2.4418 6.3082 0.875 4.375 0.875Z" fill="#525252"/>
        </svg>
      `;

      Object.assign(icon.style, {
        position: "absolute",
        right: "4px",
        top: "calc(50% + 2px)",
        height: "13px",
        alignItems: "center",
        transform: "translateY(-50%)",
        display: "none",
        pointerEvents: "none",
        lineHeight: "0"
      });

      const svg = icon.querySelector("svg");
      if (svg) svg.style.display = "block";

      span.appendChild(icon);
    }

    /* ---------- hover ---------- */
    if (searchAble) {
      span.onmouseenter = () => {
        if (!(activeSpanRef.current === span && showSearchBox)) {
          span.style.background = varStyle.hoverColor || "rgba(255,212,118,.2)";
        }
        // span.style.paddingRight = "22px";
        if (icon) {
          icon.style.display = "flex";
          // span.style.paddingRight = "22px";
        }
        span.style.borderRadius = "4px";
      };

      span.onmouseleave = () => {
        if (activeSpanRef.current !== span) {
          span.style.background = "transparent";
          span.style.borderRadius = "0";
          if (icon) {
            icon.style.display = "none";
            // span.style.paddingRight = "4px";
          }
        }
      };

      /* ---------- active ---------- */
      span.onmousedown = e => {
        e.preventDefault();
        e.stopPropagation();

        // --- 1. 清掉舊 active span ---
        if (activeSpanRef.current && activeSpanRef.current !== span) {
          const prev = activeSpanRef.current;
          prev.style.background = "transparent";
          prev.style.borderRadius = "0";
          // prev.style.paddingRight = "4px";
          const prevIcon = prev.querySelector('span[data-var-icon="1"]');
          if (prevIcon) prevIcon.style.display = "none";
        }

        // --- 2. 清掉下拉狀態 ---
        setShowSearchBox(false);
        setSearch(""); // 清掉之前輸入文字
        if (activeVar && variableMap[activeVar]) {
          // reset options 為全部原始值
          variableMap[activeVar].setOptions([]);
        }

        // --- 3. 設新 active span ---
        activeSpanRef.current = span;
        span.style.paddingRight = "22px";
        setActiveVar(placeholder);
        setShowSearchBox(true);

        const rect = span.getBoundingClientRect();
        const editorRect = editorRef.current.getBoundingClientRect();
        setSearchBoxPos({
          top: rect.bottom + window.scrollY + 5,
          left: rect.left + window.scrollX
        });

        // --- 4. 更新樣式 ---
        syncVariableSpanStyles();
      };
    }
    return span;
  };

  const syncVariableSpanStyles = () => {
    if (!editorRef.current) return;
    const spans = editorRef.current.querySelectorAll('span[data-var-span="1"]');

    spans.forEach(span => {
      const placeholder = span.dataset.placeholder;
      const style = variableMap[placeholder] || {};
      const icon = span.querySelector('span[data-var-icon="1"]');

      const isActive = activeSpanRef.current === span;

      if (isActive) {
        span.style.background = style.activeColor || "#FFD476";
        span.style.borderRadius = "4px";
        if (icon) icon.style.display = "flex";
      } else {
        span.style.background = "transparent";
        span.style.borderRadius = "0";
        if (icon) icon.style.display = "none";
      }
    });
  };

  // --------- 將光標放在最後 ---------
  const placeCursorAtEnd = fragment => {
    const sel = window.getSelection();
    const lastNode = fragment.lastChild;
    if (!lastNode) return;
    const range = document.createRange();

    if (lastNode.nodeType === 3) {
      range.setStart(lastNode, lastNode.length);
    } else if (lastNode.dataset?.varSpan === "1") {
      let next = lastNode.nextSibling;
      if (!next || next.nodeType !== 3) {
        next = document.createTextNode("\u200B");
        lastNode.parentNode.insertBefore(next, lastNode.nextSibling);
      }
      range.setStart(next, next.length);
    } else {
      range.setStartAfter(lastNode);
    }

    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  };

  const capitalizeFirstTextNode = () => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    const shouldRestoreSelection = selection && selection.rangeCount > 0 && editorRef.current.contains(selection.anchorNode) && editorRef.current.contains(selection.focusNode);
    const anchorNode = shouldRestoreSelection ? selection.anchorNode : null;
    const anchorOffset = shouldRestoreSelection ? selection.anchorOffset : 0;
    const focusNode = shouldRestoreSelection ? selection.focusNode : null;
    const focusOffset = shouldRestoreSelection ? selection.focusOffset : 0;
    let updated = false;

    const capitalizeNode = parentNode => {
      parentNode.childNodes.forEach(node => {
        if (updated) return;

        if (node.nodeType === Node.TEXT_NODE) {
          const index = node.textContent.search(/\S/);
          if (index < 0) return;

          const current = node.textContent.charAt(index);
          const next = current.toUpperCase();
          if (current !== next) {
            node.textContent = node.textContent.slice(0, index) + next + node.textContent.slice(index + 1);
          }
          updated = true;
          return;
        }

        if (node.nodeType === Node.ELEMENT_NODE && node.dataset?.varSpan === "1") {
          updated = true;
          return;
        }

        if (node.nodeType === Node.ELEMENT_NODE) {
          capitalizeNode(node);
        }
      });
    };

    capitalizeNode(editorRef.current);

    if (shouldRestoreSelection && anchorNode?.isConnected && focusNode?.isConnected) {
      selection.setBaseAndExtent(anchorNode, anchorOffset, focusNode, focusOffset);
    }
  };

  // --------- 輸入處理 ---------
  const handleInput = () => {
    capitalizeFirstTextNode();
    updateGhost();
    scheduleSearchNarrative();
  };

  const comboRef = useRef(null);

  useEffect(() => {
    if (showSearchBox && activeVar) {
      setTimeout(() => {
        if (comboRef.current) {
          comboRef.current.focus();

          // ✅ 先加载数据
          callSearchVariable(activeVar, "");

          // ✅ 再打开 dropdown
          setTimeout(() => {
            comboRef.current?.toggleBtnClick();
          }, 100); // 等数据回来一点点时间
        }
      }, 0);
    }
  }, [showSearchBox, activeVar]);

  const insertVariableAtRange = (range, placeholder, varName) => {
    if (!range || !placeholder) return;

    const sel = window.getSelection();
    insertedValueNodeRef.current = null;

    const span = createVariableSpan(varName, placeholder, true);

    range.deleteContents();
    range.insertNode(span);

    const space = document.createTextNode("\u200B");
    span.parentNode.insertBefore(space, span.nextSibling);

    // 👉 光标移动
    const newRange = document.createRange();
    newRange.setStart(space, 1);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);

    // =========================
    // ✅ 新增：激活 + 打开 searchBox
    // =========================

    // 清掉旧 active
    if (activeSpanRef.current && activeSpanRef.current !== span) {
      const prev = activeSpanRef.current;
      prev.style.background = "transparent";
      prev.style.borderRadius = "0";
      const prevIcon = prev.querySelector('span[data-var-icon="1"]');
      if (prevIcon) prevIcon.style.display = "none";
    }

    activeSpanRef.current = span;
    setActiveVar(placeholder);

    // 计算位置
    const rect = span.getBoundingClientRect();
    setSearchBoxPos({
      top: rect.bottom + window.scrollY + 5,
      left: rect.left + window.scrollX
    });

    // 打开 searchBox
    setShowSearchBox(true);

    syncVariableSpanStyles();

    // =========================

    updateGhost();
  };

  // --- 1. 抽離 Hint 處理邏輯 (不改動 DOM) ---
  const updateHintState = text => {
    let textForHint = text;
    Object.keys(variableMap).forEach(key => {
      const label = variableMap[key].label;
      if (label) textForHint = textForHint.replace(new RegExp(key, "g"), label);
    });

    const normalize = s =>
      s
        .toLowerCase()
        .replace(/\u00a0|\u200b|\u200c|\u200d|\ufeff/g, "")
        .replace(/\s+/g, " ")
        .trim();
    const normalizedInput = normalize(textForHint);

    const best = suggestionsRef.current.find(s => normalize(s).startsWith(normalizedInput));

    if (best && normalizedInput) {
      let bestWithLabel = best;
      Object.keys(variableMap).forEach(key => {
        const label = variableMap[key].label;
        bestWithLabel = bestWithLabel.replace(new RegExp(key, "g"), label);
      });
      const hintText = bestWithLabel.slice(textForHint.length);
      if (!hintText) {
        setCurrentHint(null);
        return;
      }
      setCurrentHint({
        text: hintText,
        placeholder: Object.keys(variableMap).find(key => bestWithLabel.includes(variableMap[key].label))
      });
    } else {
      setCurrentHint(null);
    }
  };

  // 1. 在組件內部上方定義 timer
  const timerRef = useRef(null);

  const updateGhost = () => {
    if (!editorRef.current || isComposing) return;

    // --- 第一部分：即時處理 (不涉及外部同步，不會跳動) ---
    let text = "";
    const processNode = parentNode => {
      parentNode.childNodes.forEach(node => {
        if (node.nodeType === 3) {
          text += node.textContent;
        } else if (node.nodeType === 1) {
          if (node.dataset?.varSpan === "1") {
            text += node.dataset.placeholder;
          } else if (node.tagName === "BR") {
            text += "\n";
          } else {
            processNode(node);
          }
        }
      });
    };
    processNode(editorRef.current);
    setEditorDisplayText(editorRef.current.innerText || "");

    // 立刻更新灰色的 Ghost 提示文字 (這是在編輯器內部的 state，不會導致重繪)
    updateHintState(text);

    // --- 第二部分：延遲同步 (Debounce) ---
    // 清除上一次的計時器
    if (timerRef.current) clearTimeout(timerRef.current);

    // 使用者停止打字 250ms 後，才執行同步
    timerRef.current = setTimeout(() => {
      let dataForParent = serializeGhostTemplateText(text);
      const firstIdx = dataForParent.search(/\S/);
      if (firstIdx >= 0) {
        dataForParent = dataForParent.slice(0, firstIdx) + dataForParent[firstIdx].toUpperCase() + dataForParent.slice(firstIdx + 1);
      }

      // 開啟鎖定，阻止 useEffect 裡的 renderTextWithVariables 執行
      isInternalChange.current = true;

      // 將資料傳給父組件
      lastEmittedValueRef.current = dataForParent;
      onChange({ value: dataForParent });

      // 鎖定 500ms，確保父組件的更新浪潮已經結束
      setTimeout(() => {
        isInternalChange.current = false;
      }, 500);
    }, 250); // 250ms 是打字體感最平衡的時間
  };

  const handleBlur = async () => {
    if (!editorRef.current) return;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    let finalValue = getInternalValue();
    let found = false;

    const capitalizeDOM = parentNode => {
      parentNode.childNodes.forEach(node => {
        if (found) return;

        if (node.nodeType === 3) {
          const idx = node.textContent.search(/\S/);
          if (idx >= 0) {
            const t = node.textContent;
            node.textContent = t.slice(0, idx) + t.charAt(idx).toUpperCase() + t.slice(idx + 1);
            found = true;
          }
        } else if (node.nodeType === 1 && node.dataset?.varSpan === "1") {
          found = true;
        } else if (node.nodeType === 1) {
          capitalizeDOM(node);
        }
      });
    };

    capitalizeDOM(editorRef.current);

    isInternalChange.current = true;
    const capitalized = finalValue.charAt(0).toUpperCase() + finalValue.slice(1);
    if (lastEmittedValueRef.current !== capitalized) {
      lastEmittedValueRef.current = capitalized;
      onChange({ value: capitalized });
    }
    setTimeout(() => {
      isInternalChange.current = false;
    }, 100);

    if (validateOnBlur && narrativeOcgEnabled && capitalized.trim()) {
      onValidationChange?.({
        visible: false,
        isLoading: true,
        keyword: "",
        reason: ""
      });
      try {
        const response = await validateTimesheetNarrative(item || {}, capitalized);
        const hint = normalizeTimesheetValidationResponse(response, capitalized);

        if (hint.visible) {
          onBlockOutsideClose?.(hint.severity === "error");
          onValidationChange?.(hint);
          return;
        }
      } catch (error) {
        console.error("Validate timesheet narrative failed:", error);
      }
    }

    onBlockOutsideClose?.(false);
    onValidationChange?.({ visible: false, keyword: "", reason: "" });
  };

  const handleFocus = () => {
    if (isFormInline) {
      setIsEditing(true);
    }
  };

  // --------- 快捷鍵處理 ---------
  const handleKeyDown = e => {
    if ((e.key === "Tab" || e.key === "ArrowRight") && currentHint) {
      e.preventDefault();
      applyHint();
      return;
    }

    const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;

    // --- 快捷键映射 ---
    // keyCode 对应：O=79, C=67, M=77
    const keyCodeMap = {
      79: "ct__", // O
      67: "cn__", // C
      77: "mn__" // M
    };

    const isTrigger = isMac ? e.metaKey && e.altKey && keyCodeMap[e.keyCode] : e.altKey && keyCodeMap[e.keyCode];

    if (isTrigger) {
      e.preventDefault();
      const varKey = keyCodeMap[e.keyCode];
      const sel = window.getSelection();
      const range = sel.getRangeAt(0);
      insertVariableAtRange(range, varKey, variableMap[varKey]?.label);
      updateGhost();
    }
  };
  const applyHint = () => {
    if (!currentHint || !editorRef.current) return;

    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;

    const range = sel.getRangeAt(0);
    const node = sel.anchorNode;

    if (node.nodeType !== Node.TEXT_NODE) return;

    const offset = sel.anchorOffset;
    const textBeforeCursor = node.textContent.slice(0, offset);

    // 建立 DocumentFragment 準備插入
    const fragment = document.createDocumentFragment();

    const hintText = currentHint.text || "";
    if (hintText) {
      const labelMap = {};
      variableKeys.forEach(key => {
        const label = variableMap[key]?.label;
        if (label) labelMap[label] = key;
      });

      const labelRegex = new RegExp(
        Object.keys(labelMap)
          .sort((a, b) => b.length - a.length)
          .map(l => l.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
          .join("|"),
        "g"
      );

      let lastIndex = 0;
      let match;

      while ((match = labelRegex.exec(hintText)) !== null) {
        let before = hintText.slice(lastIndex, match.index);
        const label = match[0];
        const key = labelMap[label];
        const { replaceData, searchAble } = variableMap[key];

        let newNode = null;
        let isAggregated = false;

        if (replaceData) {
          newNode = createVariableSpan(replaceData, key, false);
        } else {
          let displayVal = label;
          newNode = createVariableSpan(displayVal, key, searchAble);
        }

        if (isAggregated && before) {
          before = before.replace(/(?:,\s*)$/, "");
        }

        if (before) fragment.appendChild(document.createTextNode(before));
        if (newNode) fragment.appendChild(newNode);
        lastIndex = labelRegex.lastIndex;
      }

      const after = hintText.slice(lastIndex);
      if (after) fragment.appendChild(document.createTextNode(after));
    }

    // 執行插入
    node.textContent = textBeforeCursor;
    const lastInsertedNode = fragment.lastChild;
    if (node.nextSibling) {
      node.parentNode.insertBefore(fragment, node.nextSibling);
    } else {
      node.parentNode.appendChild(fragment);
    }

    // --- 關鍵修復：更精確地定位光標 ---
    const newRange = document.createRange();
    if (lastInsertedNode) {
      if (lastInsertedNode.nodeType === Node.TEXT_NODE) {
        newRange.setStart(lastInsertedNode, lastInsertedNode.length);
      } else {
        newRange.setStartAfter(lastInsertedNode);
      }
    } else {
      newRange.setStart(node, node.length);
    }

    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);

    // 重置狀態
    setCurrentHint(null);
    setShowSearchBox(false);
    activeSpanRef.current = null;
    syncVariableSpanStyles();

    // 使用 setTimeout 確保 DOM 渲染完畢後再同步 Ghost
    setTimeout(() => {
      updateGhost();
    }, 0);
  };

  /** dropdown itemRender */
  const itemRender = (li, itemProps) => {
    const item = itemProps.dataItem;
    if (item?.isGroupLabel)
      return (
        <div
          style={{
            padding: "0.25rem 1rem",
            fontWeight: 400,
            color: "#525252",
            fontSize: 12
          }}
        >
          {item.group}
        </div>
      );
    if (item?.isNew) return li;

    if (activeVar === "mn__") {
      return <HighlightText number={item?.number ?? ""} name={item?.name ?? ""} secondaryText={item?.client_name ?? ""} highlight={search} li={li} enableTooltip />;
    }

    const label = item?.name ? item.name : "";
    return <HighlightText name={label} highlight={search} li={li} />;
  };

  const focusEditorAtEnd = () => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();

    const range = document.createRange();
    const sel = window.getSelection();

    range.selectNodeContents(editor);
    range.collapse(false);

    sel.removeAllRanges();
    sel.addRange(range);
  };

  const focusEditorAfterInsertedValue = () => {
    const editor = editorRef.current;
    const insertedValueNode = insertedValueNodeRef.current;
    insertedValueNodeRef.current = null;

    if (!editor || !insertedValueNode?.isConnected || !editor.contains(insertedValueNode)) {
      focusEditorAtEnd();
      return;
    }

    editor.focus();

    const range = document.createRange();
    const sel = window.getSelection();

    range.setStart(insertedValueNode, insertedValueNode.textContent?.length ?? 0);
    range.collapse(true);

    sel.removeAllRanges();
    sel.addRange(range);
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        display: "block"
      }}
    >
      <div
        style={{
          overflowY: "auto",
          overflowX: "hidden",
          position: "relative",
          width: "100%",
          boxSizing: "border-box",
          scrollbarGutter: "stable"
        }}
      >
        <div
          className="editor-content"
          ref={editorRef}
          contentEditable={!disabled}
          aria-disabled={disabled}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={e => {
            setIsComposing(false);
            handleInput(e);
          }}
          onInput={e => {
            if (!isComposing) handleInput(e);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setIsFocused(true);
            handleFocus?.();
          }}
          onBlur={() => {
            setIsFocused(false);
            handleBlur?.();
          }}
          data-ghost-hint={currentHint?.text || undefined}
          style={{
            minHeight: "150px",
            borderRadius: 4,
            border: isFormInline ? "none" : isFocused ? "1px solid #865BDF" : "1px solid #EBEBEB",
            padding: "10px 0 10px 10px",
            fontSize: "14px",
            fontKerning: "none",
            lineHeight: "1.5",
            width: "100%",
            boxSizing: "border-box",
            outline: "none",
            wordBreak: "break-word",
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
            position: "relative",
            zIndex: 2,
            background: "transparent",
            display: "block"
          }}
        />

        {currentHint && showTemplateShortcutHint && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              padding: 0,
              fontSize: "14px",
              fontKerning: "none",
              lineHeight: "1.42",
              letterSpacing: 0,
              pointerEvents: "none",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              width: "100%",
              height: "100%",
              boxSizing: "border-box",
              zIndex: 1
            }}
          >
            <span style={{ color: "transparent" }}>{`${editorDisplayText}${currentHint.text || ""}`}</span>
            <span aria-hidden="true" className="ml-1.5 inline-block rounded border border-[#D9D9D9] bg-white px-[5px] align-[1px] text-xs leading-5 text-[#999999] shadow-[0_1px_1px_rgba(0,0,0,0.06)]">
              Tab
            </span>
          </div>
        )}
        {showSearchBox && activeVar && (
          <div
            style={{
              position: "fixed",
              top: searchBoxPos.top - window.scrollY,
              left: searchBoxPos.left - window.scrollX,
              background: "#fff",
              zIndex: 1100,
              width: "300px",
              display: "flex",
              gap: "8px",
              alignItems: "center"
            }}
          >
            <ComboBox
              disabled={disabled}
              ref={comboRef}
              data={variableMap[activeVar].options}
              textField="name"
              dataItemKey="obj_id"
              placeholder="Search"
              suggest
              filterable
              onFilterChange={event => {
                const value = event.filter?.value ?? "";
                setSearch(value);
                if (variableMap[activeVar]) {
                  const filtered = filterBy(variableMap[activeVar].options, event.filter);
                  variableMap[activeVar].setOptions(filtered);
                }

                callSearchVariable(activeVar, value);
              }}
              itemRender={itemRender}
              onChange={e => {
                if (!activeSpanRef.current) return;

                const span = activeSpanRef.current;
                const parent = span.parentNode;
                const textNode = document.createTextNode(e.value.name);
                parent.replaceChild(textNode, span);
                insertedValueNodeRef.current = textNode;

                setActiveVar(null);
                setShowSearchBox(false);
                activeSpanRef.current = null;

                updateGhost();
              }}
              onClose={() => {
                if (activeSpanRef.current) {
                  // activeSpanRef.current.style.paddingRight = "4px";
                  activeSpanRef.current.style.background = "transparent";
                  const icon = activeSpanRef.current.querySelector('span[data-var-icon="1"]');
                  if (icon) icon.style.display = "none";

                  activeSpanRef.current = null;
                  syncVariableSpanStyles();
                }
                setShowSearchBox(false);
                focusEditorAfterInsertedValue();
              }}
              onBlur={() => {
                if (activeSpanRef.current) {
                  // activeSpanRef.current.style.paddingRight = "4px";
                  activeSpanRef.current.style.background = "transparent";
                  const icon = activeSpanRef.current.querySelector('span[data-var-icon="1"]');
                  if (icon) icon.style.display = "none";

                  activeSpanRef.current = null;
                  syncVariableSpanStyles();
                }
                setShowSearchBox(false);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
});

export default GhostTemplateEditor;
